import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";

/**
 * WhatsAppFloatingButton
 * -----------------------------------------------------------------------------
 * Sticky WhatsApp CTA visible on every public page.
 * - Auto-detects the correct number based on the current route (Mauritius vs Réunion/FR)
 * - Respects safe-area-inset (iOS), prefers-reduced-motion, keyboard focus
 * - Fires Meta Pixel `WhatsAppFloatingClick` with source_page metadata
 * - Hidden on /admin routes (internal tool, no external contact needed)
 */

// --- Route-aware number resolution ------------------------------------------
const MAURICE_NUMBER = "23058535782"; // EM Group Maurice (WhatsApp Business)
const REUNION_NUMBER = "262693482110"; // EM Group Réunion (WhatsApp)

const MAURICE_ROUTE_MARKERS = [
  "/site-location-maurice",
  "/decouverte-rs-maurice",
  "/creation-logo-ile-maurice",
  "/blog/cout-site-web-maurice",
  "/blog/website-cost-mauritius",
];

function resolveContactForPath(pathname) {
  const isMaurice = MAURICE_ROUTE_MARKERS.some((p) => pathname.startsWith(p));
  if (isMaurice) {
    return {
      region: "maurice",
      number: MAURICE_NUMBER,
      display: "+230 5853 5782",
      greeting:
        "Bonjour EM Group, je vous contacte depuis votre site à Maurice — j'aimerais échanger sur mon projet.",
    };
  }
  return {
    region: "reunion",
    number: REUNION_NUMBER,
    display: "+262 693 48 21 10",
    greeting:
      "Bonjour EM Group, je vous contacte depuis votre site — j'aimerais échanger sur mon projet.",
  };
}

// --- Routes where the button is hidden --------------------------------------
const HIDDEN_PREFIXES = ["/admin"];

export default function WhatsAppFloatingButton() {
  const { pathname } = useLocation();
  const [pulseOn, setPulseOn] = useState(true);

  const hidden = HIDDEN_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  const contact = useMemo(() => resolveContactForPath(pathname), [pathname]);

  const waUrl = useMemo(
    () =>
      `https://wa.me/${contact.number}?text=${encodeURIComponent(contact.greeting)}`,
    [contact],
  );

  // Kill the pulse after first user interaction (avoids infinite motion — a11y)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stop = () => setPulseOn(false);
    window.addEventListener("scroll", stop, { once: true, passive: true });
    window.addEventListener("pointerdown", stop, { once: true });
    return () => {
      window.removeEventListener("scroll", stop);
      window.removeEventListener("pointerdown", stop);
    };
  }, [pathname]);

  const handleClick = () => {
    if (typeof window !== "undefined" && window.fbq) {
      try {
        window.fbq("trackCustom", "WhatsAppFloatingClick", {
          region: contact.region,
          source_page: pathname,
          number: contact.display,
        });
        window.fbq("track", "Contact");
      } catch (_e) {
        /* noop */
      }
    }
  };

  if (hidden) return null;

  return (
    <a
      href={waUrl}
      onClick={handleClick}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`Discuter avec EM Group sur WhatsApp (${contact.display})`}
      data-testid="wa-floating"
      data-region={contact.region}
      className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[60] inline-flex items-center gap-2 group focus:outline-none"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <span
        aria-hidden="true"
        className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur-md text-white text-xs font-medium tracking-tight pl-3 pr-3.5 py-2 border border-white/10 shadow-lg shadow-black/30 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-focus:opacity-100 transition-all duration-300"
      >
        Discutons sur WhatsApp
      </span>
      <span className="relative inline-flex">
        {pulseOn && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-emerald-500/50 motion-safe:animate-ping motion-reduce:hidden"
          />
        )}
        <span
          className="relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white shadow-lg shadow-emerald-900/30 ring-2 ring-white/10 group-hover:scale-105 group-focus-visible:ring-4 group-focus-visible:ring-emerald-400/40 transition-transform duration-200"
        >
          <MessageCircle size={26} strokeWidth={2.2} />
        </span>
      </span>
    </a>
  );
}
